<script setup>
import Buttons from '@/components/ui/Buttons.vue';
defineProps({
  url: {
    type: String,
    default: () => '',
  },
  inStock: {
    type: Boolean,
    default: () => true,
  },
  emptyImage: {
    type: Boolean,
    default: () => false,
  },
  image: {
    type: String,
    default: () => '',
  },
  name: {
    type: String,
    default: () => '',
  },
  price: {
    type: String,
    default: () => '',
  },
  currentPrice: {
    type: String,
    default: () => '',
  },
  currency: {
    type: String,
    default: () => '',
  },
  discount: {
    type: String,
    default: () => '',
  },
  maxPayment: {
    type: String,
    default: () => '',
  },
  monthlyPayment: {
    type: String,
    default: () => '',
  },
  kasko: {
    type: String,
    default: () => '',
  },
  tooltip: {
    type: String,
    default: () => '',
  },
  calculatorLink: {
    type: String,
    default: () => '',
  },
  parameters: {
    type: Array,
    default: () => [],
  },
  specs: {
    type: Object,
    default: () => {},
  },
  view: {
    type: String,
    required: true,
    default: 'default',
    validator: (value) => {
      return ['default', 'special', 'compact', 'equipment', 'stock'].includes(
        value,
      );
    },
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
  isStockCar: {
    type: Boolean,
    default: () => false,
  },
  asHeading: {
    type: Boolean,
    default: true,
  },
});

const formatPrice = (price) => {
  return price.toLocaleString('uk-UA');
};
</script>
<template>
  <div
    class="car-item"
    :class="`car-item--${view} car-item--${backgroundColor}`"
  >
    <template v-if="view === 'compact'">
      <CTagDetect v-if="url" :to="url" class="car-item__wrap">
        <div class="car-item__media">
          <div
            :class="[
              'car-item__media-frame',
              emptyImage ? 'car-item__media-frame--brand' : '',
            ]"
          >
            <NuxtImg
              v-if="image && image.src"
              :src="image.src"
              :alt="image.alt"
              :height="image.height"
              :width="image.width"
              :class="[
                'car-item__media-img',
                emptyImage ? 'car-item__media-img--brand' : '',
              ]"
              loading="lazy"
            />
          </div>
        </div>
        <p
          v-if="name && asHeading"
          class="mb-0 car-item__title"
          v-html="name"
        ></p>
        <p v-else-if="name" class="mb-0 car-item__title" v-html="name"></p>
      </CTagDetect>
      <div v-else class="car-item__wrap">
        <div class="car-item__image">
          <NuxtImg
            v-if="image && image.src"
            :src="image?.src"
            :alt="image?.alt"
            :height="image.height"
            :width="image.width"
            loading="lazy"
          />
        </div>
        <p
          v-if="name && asHeading"
          class="mb-0 car-item__title"
          v-html="name"
        ></p>
        <p v-else-if="name" class="mb-0 car-item__title" v-html="name"></p>
      </div>
    </template>
    <template v-else>
      <div class="car-item__wrap">
        <CTagDetect :to="url" class="car-item__media">
          <div
            :class="[
              'car-item__media-frame',
              emptyImage ? 'car-item__media-frame--brand' : '',
            ]"
          >
            <NuxtImg
              v-if="image && image.src"
              :src="image.src"
              :alt="image.alt"
              :height="image.height"
              :width="image.width"
              :class="[
                'car-item__media-img',
                emptyImage ? 'car-item__media-img--brand' : '',
              ]"
              loading="lazy"
            />
          </div>
        </CTagDetect>
        <div v-if="inStock && view !== 'equipment'" class="car-item__instock">
          <CIcon
            :name="backgroundColor === 'black' ? 'check-white' : 'check'"
            width="12"
            height="12"
            class="car-item__instock-icon"
          />
          <span class="car-item__instock-text">{{ $t('car.available') }}</span>
        </div>
        <CTagDetect :to="url">
          <p
            v-if="name && asHeading"
            class="mb-0 car-item__title"
            v-html="name"
          ></p>
          <p v-else-if="name" class="mb-0 car-item__title" v-html="name"></p>
        </CTagDetect>
        <div class="car-item__info">
          <div class="car-item__price">
            <div class="car-item__price-item">
              <span class="car-item__price-current">
                <template v-if="currentPrice">
                  <template v-if="isStockCar">
                    {{ formatPrice(currentPrice) }} {{ currency }}
                  </template>
                  <template v-else>
                    <template v-if="!discount">{{ $t('car.from') }}</template>
                    {{ formatPrice(currentPrice) }} {{ currency }}
                  </template>
                </template>
                <template v-else>{{ $t('car.price_empty') }}</template>
              </span>
            </div>
            <div v-if="discount" class="car-item__price-item">
              <span v-if="price" class="car-item__price-original"
                >{{ formatPrice(price) }} {{ currency }}</span
              >
              <span v-if="discount" class="car-item__discount"
                >-{{ discount }}%</span
              >
            </div>
          </div>
          <div
            v-if="(monthlyPayment && maxPayment) || kasko"
            class="car-item__installments"
          >
            <p
              v-if="monthlyPayment && maxPayment"
              class="mb-0"
              :class="['car-item__installment' ?? tooltip]"
            >
              {{ $t('car.payments_part1', { payments: maxPayment }) }}
              {{ formatPrice(monthlyPayment) }}
              {{ $t('car.payments_part2') }}
              <CIcon
                v-if="tooltip"
                v-tooltip.top="tooltip"
                class="car-item__installment-tooltip"
                name="info"
                width="12"
                height="12"
              />
            </p>
            <p v-if="kasko" class="mb-0">
              {{ $t('car.kasko', { kasko }) }}
            </p>
          </div>
          <div
            v-if="parameters && view === 'equipment'"
            class="car-item__parameters"
          >
            <div
              v-for="item in parameters"
              :key="item.title"
              class="car-item__parameter"
            >
              <div
                class="car-item__parameter-title body-3-regular"
                v-html="item.title"
              ></div>
              <div
                class="car-item__parameter-value body-3-medium"
                v-html="item.value"
              ></div>
            </div>
          </div>

          <div v-if="specs" class="car-item__specs">
            <template v-for="(item, itemIndex) in specs" :key="itemIndex">
              <template v-if="itemIndex === 'color'">
                <div v-if="item.title" class="car-item__spec">
                  <span
                    class="car-item__spec-color"
                    :style="{ 'background-color': item.value }"
                  ></span>
                  <span class="car-item__spec-value body-3-regular">
                    {{ item.title }}
                  </span>
                </div>
              </template>
              <template v-else>
                <div v-if="item" class="car-item__spec">
                  <CIcon
                    :name="
                      backgroundColor === 'black'
                        ? `specs-${itemIndex}-white`
                        : `specs-${itemIndex}`
                    "
                    width="12"
                    height="12"
                    class="car-item__spec-icon"
                  />
                  <span class="car-item__spec-value body-3-regular">
                    {{ item }}
                  </span>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
      <Buttons
        v-if="view === 'equipment'"
        class="car-item__buttons"
        template="equipment"
        :buttons-prefix-class="'js-button-car-item'"
        :buttons="[
          {
            id: 1,
            type: 'popup',
            title: $t('car.get_consultation'),
            popup: 'consultation',
          },
          {
            id: 2,
            type: 'link',
            title: $t('car.calculate_payments'),
            link: {
              url: calculatorLink,
              target: '_self',
              rel: '',
              is_external: false,
            },
          },
        ]"
      />
      <div v-else class="car-item__buttons">
        <Button v-slot="slotProps" as-child rounded>
          <CTagDetect
            :to="url"
            :class="[
              slotProps.class,
              'car-item__button',
              backgroundColor === 'black' ? 'button-white' : 'button-black',
              `js-button-car`,
            ]"
            >{{ $t('car.more_details') }}</CTagDetect
          >
        </Button>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
.car-item {
  $parent: &;
  align-items: center;
  color: $black;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  padding: 16px;
  text-align: center;
  @include media-breakpoint-up(sm) {
    gap: 40px;
  }
  @include media-breakpoint-up(md) {
    &:hover {
      #{$parent} {
        &__title {
          color: $primary;
        }
      }
    }
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(padding, 16);
    @include responsive-spacing(gap, 40);
  }
  &__wrap {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__media {
    width: 100%;
    &-frame {
      aspect-ratio: 296/224;
      align-items: center;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      overflow: hidden;
      width: 100%;
      @include media-breakpoint-up(sm) {
        aspect-ratio: 244/185;
        max-width: 75%;
        margin: 0 auto;
      }
      @include media-breakpoint-up(md) {
        aspect-ratio: 248/185;
        max-width: 70.1%;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 4);
      }
      &--brand {
        aspect-ratio: unset;
        @include media-breakpoint-up(sm) {
          aspect-ratio: 244/185;
          margin: 0 auto;
        }
        @include media-breakpoint-up(md) {
          aspect-ratio: 248/185;
        }
      }
    }
    &-img {
      border-radius: 4px;
      display: block;
      height: 100%;
      object-fit: contain;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 4);
      }
      &--brand {
        object-fit: cover;
        height: auto;
        width: auto;
        @include media-breakpoint-up(md) {
          width: 25%;
        }
      }
    }
  }
  &__instock {
    align-items: center;
    border: 1px solid $divider;
    border-radius: 2px;
    display: grid;
    grid-template-columns: 12px auto;
    gap: 8px;
    margin: 0 auto;
    padding: 3px 8px 2px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-width, 1);
      @include responsive-spacing(border-radius, 2);
      @include responsive-spacing(gap, 8);
      @include responsive-spacing(padding-top, 3);
      @include responsive-spacing(padding-bottom, 2);
      @include responsive-spacing(padding-left, 8);
      @include responsive-spacing(padding-right, 8);
      grid-template-columns: px-to-vw(12) auto;
    }
    &-icon {
      height: auto;
      width: 100%;
    }
    &-text {
      @include body-3-medium;
    }
  }
  &__title {
    @include h4;
    margin: 0;
    transition: all 0.3s linear;
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__price {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: center;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
    &-item {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }
    &-current {
      @include body-1-bold;
    }
    &-original {
      @include label;
      color: $on-surface-variant-2;
    }
  }
  &__discount {
    @include body-3-medium;
    border-radius: 2px;
    background-color: $primary;
    color: $white;
    padding: 5px 5px 1px 4px;
    position: relative;
    z-index: 1;
    &:before {
      background-color: inherit;
      content: '';
      height: 6px;
      left: -1px;
      position: absolute;
      top: calc(50% - 3px);
      transform: rotate(45deg);
      width: 6px;
      z-index: -1;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 2);
      @include responsive-spacing(padding-top, 5);
      @include responsive-spacing(padding-right, 5);
      @include responsive-spacing(padding-bottom, 1);
      @include responsive-spacing(padding-left, 4);
      &:before {
        @include responsive-spacing(height, 6);
        @include responsive-spacing(left, -1);
        @include responsive-spacing(width, 6);
        top: calc(50% - px-to-vw(3));
      }
    }
  }
  &__installments {
    @include body-3-regular;
    display: flex;
    flex-direction: column;
    gap: 2px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 2);
    }
  }
  &__installment {
    align-items: center;
    display: grid;
    gap: 8px;
    grid-template-columns: auto 12px;
    margin: 0 auto;
    width: fit-content;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
      grid-template-columns: auto px-to-vw(12);
    }
    &-tooltip {
      height: auto;
      width: 100%;
    }
  }
  &__buttons {
    display: flex;
    flex-direction: column;
    justify-items: center;
    gap: 8px;
    margin-top: auto;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__button {
    @include link-normal;
  }
  &__parameters {
    display: grid;
    grid-auto-flow: column;
    gap: 8px;
    padding: 16px 24px 0;
    position: relative;
    &::before {
      border-top: 1px solid $divider;
      content: '';
      left: 24px;
      position: absolute;
      width: calc(100% - 48px);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
      @include responsive-spacing(padding-top, 16);
      @include responsive-spacing(padding-left, 24);
      @include responsive-spacing(padding-right, 24);
      &::before {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(left, 24);
        width: calc(100% - px-to-vw(48));
      }
    }
  }
  &__parameter {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 4);
    }
    &-title {
      color: $on-surface-variant-2;
    }
    &-value {
      color: $black;
    }
  }
  &__specs {
    display: flex;
    flex-direction: column;
    gap: 2px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 2);
    }
  }
  &__spec {
    align-items: center;
    display: grid;
    grid-template-columns: 12px auto;
    gap: 4px;
    justify-content: center;
    text-align: center;
    @include media-breakpoint-up(xl) {
      grid-template-columns: px-to-vw(12) auto;
      @include responsive-spacing(gap, 4);
    }
    &-icon {
      aspect-ratio: 1/1;
      height: auto;
      width: 100%;
    }
    &-color {
      aspect-ratio: 1/1;
      border: 1px solid $black;
      height: auto;
      width: 100%;
    }
    &-value {
      color: $black;
    }
  }
  &--equipment {
    #{$parent} {
      &__price {
        @include body-1-bold;
      }
    }
  }
  &--compact {
    padding: 0 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-left, 4);
      @include responsive-spacing(padding-right, 4);
    }
    #{$parent} {
      &__title {
        @include body-2-bold;
      }
      &__media {
        &-frame {
          aspect-ratio: 146/104;
          max-width: 90%;
          &--brand {
            aspect-ratio: 146/104;
          }
        }
      }
      &__wrap {
        gap: 16px;
        width: 100%;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 16);
        }
      }
    }
  }
  &--black {
    color: $white;
  }
}
</style>
